/**
 * Una tarea en la lista.
 *
 * La misma fila sirve en la pantalla de Tareas y en el bloque que llevan las
 * fichas de lead y propietario, con `compacta` para lo segundo: ahí la relación
 * sobra —se está mirando justo esa ficha— y el espacio es el de una columna.
 */
import { Link } from 'react-router-dom';
import { Check, RotateCcw, Clock, AlertTriangle, Repeat, Bell, ChevronRight } from 'lucide-react';
import type { Task } from '@/lib/types';
import { classNames } from '@/lib/utils';
import {
  COLOR_PRIORIDAD, ETIQUETA_PRIORIDAD, ETIQUETA_TIPO, ICONO_TIPO, PUNTO_PRIORIDAD,
  clasificar, rutaDeRelacion, textoVencimiento,
} from './tipos';

interface Props {
  tarea: Task;
  compacta?: boolean;
  seleccionada?: boolean;
  onSeleccionar?: (id: string, seleccionada: boolean) => void;
  onCompletar: (tarea: Task) => void;
  onReabrir: (tarea: Task) => void;
  onAbrir: (tarea: Task) => void;
  onAplazar?: (tarea: Task, dias: number) => void;
}

export default function FilaTarea({
  tarea, compacta, seleccionada, onSeleccionar, onCompletar, onReabrir, onAbrir, onAplazar,
}: Props) {
  const cerrada = tarea.status !== 'pending';
  const vencida = clasificar(tarea) === 'vencida';
  const IconoTipo = ICONO_TIPO[tarea.tipo];
  const ruta = rutaDeRelacion(tarea);

  return (
    <div
      className={classNames(
        'card flex items-start gap-3 transition-colors',
        compacta && '!p-3',
        seleccionada && 'ring-2 ring-sereno-dark/20',
      )}
    >
      {onSeleccionar && (
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-gray-300 accent-sereno-dark flex-shrink-0"
          checked={!!seleccionada}
          onChange={e => onSeleccionar(tarea.id, e.target.checked)}
          aria-label={`Seleccionar «${tarea.title}»`}
        />
      )}

      <button
        onClick={() => (cerrada ? onReabrir(tarea) : onCompletar(tarea))}
        className={classNames(
          'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors group',
          cerrada
            ? 'bg-green-500 hover:bg-green-600'
            : 'border-2 border-gray-300 hover:border-green-500 hover:bg-green-50',
        )}
        title={cerrada ? 'Reabrir la tarea' : 'Marcar como completada'}
        aria-label={cerrada ? 'Reabrir la tarea' : 'Marcar como completada'}
      >
        {cerrada
          ? <Check size={12} className="text-white group-hover:hidden" />
          : <Check size={12} className="text-transparent group-hover:text-green-500" />}
        {cerrada && <RotateCcw size={11} className="text-white hidden group-hover:block" />}
      </button>

      {/* Todo el cuerpo abre la edición: buscar un lápiz diminuto en cada fila
          es peor que poder pinchar en cualquier sitio. */}
      <button onClick={() => onAbrir(tarea)} className="flex-1 min-w-0 text-left">
        <div className="flex items-start gap-2">
          <span className={classNames('w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0', PUNTO_PRIORIDAD[tarea.priority])}
            title={`Prioridad ${ETIQUETA_PRIORIDAD[tarea.priority].toLowerCase()}`} />
          <p className={classNames('text-sm font-medium break-words', cerrada && 'line-through text-sereno-dark/40')}>
            {tarea.title}
          </p>
        </div>

        {tarea.description && !compacta && (
          <p className="text-xs text-sereno-dark/40 mt-0.5 line-clamp-2">{tarea.description}</p>
        )}

        <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 flex-wrap text-xs">
          <span className="flex items-center gap-1 text-sereno-dark/40">
            <IconoTipo size={12} /> {ETIQUETA_TIPO[tarea.tipo]}
          </span>

          <span className={classNames(
            'flex items-center gap-1',
            vencida ? 'text-red-500 font-medium' : 'text-sereno-dark/40',
          )}>
            {vencida ? <AlertTriangle size={12} /> : <Clock size={12} />}
            {textoVencimiento(tarea.dueDate)}
          </span>

          {tarea.responsable && (
            <span className="text-sereno-dark/40">{tarea.responsable}</span>
          )}

          {tarea.recurrencia !== 'none' && (
            <span className="flex items-center gap-1 text-sereno-dark/40" title="Se repite al completarla">
              <Repeat size={12} />
            </span>
          )}

          {tarea.remindAt && (
            <span className="flex items-center gap-1 text-sereno-dark/40" title={`Aviso: ${textoVencimiento(tarea.remindAt)}`}>
              <Bell size={12} />
            </span>
          )}

          {!compacta && !tarea.relatedId && (
            <span className={classNames('hidden sm:inline', COLOR_PRIORIDAD[tarea.priority])}>
              {ETIQUETA_PRIORIDAD[tarea.priority]}
            </span>
          )}
        </div>
      </button>

      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {/* El enlace va fuera del botón del cuerpo: anidar un <a> dentro de un
            <button> no es HTML válido y el clic se lo comería el botón. */}
        {!compacta && ruta && (
          <Link
            to={ruta}
            className="flex items-center gap-1 text-xs text-sereno-dark/50 hover:text-sereno-dark max-w-[10rem] sm:max-w-[14rem] truncate"
            title={tarea.relatedName || undefined}
          >
            <span className="truncate">{tarea.relatedName || 'Ver ficha'}</span>
            <ChevronRight size={12} className="flex-shrink-0" />
          </Link>
        )}

        {onAplazar && !cerrada && (
          <div className="flex gap-1">
            <button
              onClick={() => onAplazar(tarea, 1)}
              className="px-1.5 py-0.5 text-xs rounded text-sereno-dark/40 hover:bg-gray-100 hover:text-sereno-dark transition-colors"
              title="Aplazar un día"
            >
              +1d
            </button>
            <button
              onClick={() => onAplazar(tarea, 7)}
              className="px-1.5 py-0.5 text-xs rounded text-sereno-dark/40 hover:bg-gray-100 hover:text-sereno-dark transition-colors"
              title="Aplazar una semana"
            >
              +7d
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
